import { defineStore } from 'pinia';
import { useAuthStore } from './AuthStore';
import { useNodeStore } from './NodeStore';
import { usePlayerStore } from './PlayerStore';
import { useCommonStore } from './CommonStore';
import { useSearchStore } from './SearchStore';
import { usePodCommentStore } from './PodCommentStore';
import { useRecycleBinStore } from './RecycleBinStore';
import { useUploadStore } from './UploadStore';
import Api from '../api/Api';
import { v4 as uuidv4 } from 'uuid';
import MimetypeHelper from '../components/MimetypeHelper';
import CreateManyPodsDuplicateUuidsError from '@/stores/error/CreateManyPodsDuplicateUuidsError.js';

export const usePodStore = defineStore('pod', {
  state: () => ({
    viewPodMeta: [],
    viewPodActions: [],
    viewPodMoved: false,
    viewPodDeleted: false,
    viewPodArchived: false,
    activeToolbar: 0,     // active toolbar on the side menu, int

    viewPodId: null,
    viewPodArchiveContents: null,
    viewPodListArchiveContentsError: null,
    isRefreshingPodArchiveContents: false,

    allPods: [],
    selectedPodIds: [],
    selectedAssetIds: [],

    quickSwitcherVisible: false,
    latestDeletedPodIds: [],
  }),
  getters: {
    getPodsByNodeId: function(state) {
      return function (nodeID) {
        return state.allPods.filter(x => x.nodeid === nodeID);
      }
    },
    getPodById: function(state) {
      return function (podID) {
        return state.allPods.find(x => x.id === podID);
      }
    },
    getPods: function(state) {
      const nodeStore = useNodeStore();
      const currentNode = nodeStore.getCurrentNode;

      if (currentNode && currentNode.id) {
        return state.allPods
        .filter(x => x.nodeid === currentNode.id)
        // .sort(function(a, b) {
        //   return a.name.localeCompare(b.name)
        // });
      }
      return [];
    },
    getPodVersions: function() {
      return (podId) => {
        let relatedPods = [];
        let pod = this.getPods.find(x => x.id === podId);

        if (!pod) {
          return relatedPods;
        }

        let parentPodId = pod.parentpodid ? pod.parentpodid : pod.id;

        let parentPod = this.getPods.find(x => x.id === parentPodId);
        relatedPods.push(parentPod);

        let otherPods = this.getPods.filter(x => x.parentpodid === parentPod.id);
        relatedPods = relatedPods.concat(otherPods);

        relatedPods.sort(function(a, b) {
          if (a.order > b.order) return 1;
          if (a.order < b.order) return -1;
          return 0;
        });

        return relatedPods;
      };
    },
    getViewPod: function(state) {
      if (!state.viewPodId) {
        return null;
      }
      return state.allPods.find(x => x.id === state.viewPodId);
    },
    getSelectedPods: function(state) {
      return state.allPods.filter(x => state.selectedPodIds.indexOf(x.id) >= 0);
    },
    getSelectedPodIds: function (state) {
      return state.selectedPodIds;
    },
    getSelectedVideoPods: function() {
      return this.getSelectedPods.filter(item => MimetypeHelper.isMimetypeVideo(item));
    },
    getSelectedAudioPods: function() {
      return this.getSelectedPods.filter(item => MimetypeHelper.isMimetypeAudio(item));
    },
    getSelectedImagePods: function() {
      return this.getSelectedPods.filter(item => MimetypeHelper.isMimetypeImage(item));
    },
    getSelectedAssets: function(state) {
      if (!this.getViewPod || !this.getViewPod.asset) {
        return [];
      }

      let selectedAssets = [];

      // we assume that the selected assets are part of the view pod
      for (const assetType in this.getViewPod.asset) {
        let selectedItems = this.getViewPod.asset[assetType].filter(x => state.selectedAssetIds.indexOf(x.id) >= 0);
        selectedAssets = selectedAssets.concat(selectedItems);
      }

      return selectedAssets;
    },
    getPodMeta: function(state) {
      return state.viewPodMeta;
    },
    getPodActions: function(state) {
      return state.viewPodActions;
    },
    isViewPodMoved: function(state) {
      return state.viewPodMoved;
    },
    isViewPodDeleted: function(state) {
      return state.viewPodDeleted;
    },
    isViewPodArchived: function(state) {
      return state.viewPodArchived;
    },
    isVideoPod: function() {
      return this.getViewPod && MimetypeHelper.isMimetypeVideo(this.getViewPod);
    },
    isImagePod: function() {
      return this.getViewPod && MimetypeHelper.isMimetypeImage(this.getViewPod);
    },
    isAudioPod: function() {
      return this.getViewPod && MimetypeHelper.isMimetypeAudio(this.getViewPod);
    },
    isArchivePod: function() {
      return this.getViewPod && MimetypeHelper.isMimetypeArchive(this.getViewPod);
    },
    getSubtitleAssets: function() {
      if (!this.getViewPod || !this.getViewPod.asset) {
        return [];
      }

      let assets = []
      const types = ['srt', 'vtt'];

      const commonStore = useCommonStore();
      const STATECOMPLETE = commonStore.assetStates.COMPLETE

      for (let type of types) {
        if (!Array.isArray(this.getViewPod.asset[type])) {
          continue;
        }

        const completedAssests = this.getViewPod.asset[type].filter(x => x.state == STATECOMPLETE);
        assets = assets.concat(completedAssests);
      }

      assets.sort(function(a, b) {
        return a.name.localeCompare(b.name);
      });

      return assets;
    },
    getActiveToolbar: function(state) {
      return state.activeToolbar;
    },
    hasSelectedPods: function(state) {
      return state.selectedPodIds.length > 0;
    },
    isQuickSwitcherVisible: function(state) {
      return state.quickSwitcherVisible
    },
    getLatestDeletedPodIds: function(state) {
      return state.latestDeletedPodIds;
    },
    getStatusStateMessage: function(state) {
      return (podId) => {
        const pod = state.allPods.find(x => x.id === podId);
        if (!pod || !pod.status) {
          return null;
        }

        let messageCode = null;
        let messageParams = {};

        switch (pod.status.status) {
          case 'upload_in_progress':
            messageCode = 'PS1';
            break;
          case 'upload_failed':
            messageCode = 'PS2';
            break;
          case 'queued':
            messageCode = 'PS3';
            break;
          case 'transcoding':
            messageCode = 'PS4';
            messageParams = {
              p1: !pod.status.metadata || !pod.status.metadata.percentage
                ? '0%'
                : `${pod.status.metadata.percentage}%`,
            };
            break;
          case 'transcoding_failed':
            messageCode = 'PS5';
            break;
          case 'extracting_audio':
            messageCode = 'PS6';
            break;
          case 'generating_thumbnail':
            messageCode = 'PS7';
            break;
          case 'generating_live_preview':
            messageCode = 'PS8';
            break;
          case 'extracting_subtitles':
            messageCode = 'PS9';
            break;
          case 'completed':
            messageCode = 'PS10';
            break;
        }

        return {
          code: messageCode,
          params: messageParams,
        }
      };
    },
    getViewPodIsRefreshingPodArchiveContents: function (state) {
      return state.isRefreshingPodArchiveContents;
    },
    getViewPodListArchiveContentsError: function(state) {
      return state.viewPodListArchiveContentsError;
    },
    getViewPodArchiveContents: function(state) {
      return state.viewPodArchiveContents;
    }
  },
  actions: {
    _addOrUpdatePods: function(pods) {
      if (!Array.isArray(pods)) {
        pods = [pods];
      }

      const alteredPods = [...this.allPods];
      const podIds = new Set(alteredPods.map((item) => item.id));

      let idx;
      let alteredPodCreatedAt;
      let podCreatedAt;
      let podAdded;

      for (const pod of pods) {
        if (podIds.has(pod.id)) {
          idx = alteredPods.findIndex((item) => item.id === pod.id);
          alteredPods[idx] = pod;
        } else if (alteredPods.length === 0) {
          alteredPods.push(pod);
        } else {
          /**
           * NOTE: This assumes that the pods are sorted by "createdat" in descending order.
           */
          podCreatedAt = new Date(pod.createdat).getTime();
          podAdded = false;

          for (idx = 0; idx < alteredPods.length; idx++) {
            alteredPodCreatedAt = new Date(alteredPods[idx].createdat).getTime();

            if (podCreatedAt >= alteredPodCreatedAt) {
              alteredPods.splice(idx, 0, pod);
              podAdded = true;
              break;
            }
          }

          if (!podAdded) {
            alteredPods.push(pod);
          }
        }
      }

      if (alteredPods.length > 0) {
        this.allPods = alteredPods;
      }
    },
    _setViewPodId: function(podid) {
      this.viewPodId = podid;
      this.selectedPodIds = [];
      this.selectedAssetIds = [];
    },

    _resetViewPodEvents: function() {
      this.viewPodMoved = false;
      this.viewPodDeleted = false;
      this.viewPodArchived = false;
    },

    _setViewPodActions: function(actions) {
      actions.sort(function(a, b) {
        let dateA = new Date(a.createdat);
        let dateB = new Date(b.createdat);
        if (dateA < dateB) return 1;
        if (dateA > dateB) return -1;
        return 0;
      });

      this.viewPodActions = actions;
    },
    _movePod: function(data) {
      if (this.viewPodId && this.viewPodId === data.pod.id) {
        this.viewPodMoved = true;
      }
    },
    _deletePods: function(podIDs) {
      if (this.viewPodId && podIDs.indexOf(this.viewPodId) >= 0) {
        this.viewPodDeleted = true;
      }

      this.allPods = this.allPods.filter(x => podIDs.indexOf(x.id) < 0);
      this.selectedPodIds = this.selectedPodIds.filter(x => podIDs.indexOf(x) < 0);
    },
    _addPodAsset: function(data) {
      // console.log("CREATED POD ASSET", data);

      const assetTypes = {
        0: 'raw',
        1: 'thumbnail',
        2: 'web',
        3: 'seechtotext',
        4: 'audio',
        5: 'livepreview',
        6: 'vtt',
        7: 'srt',
        8: 'external',
        9: 'frame',
        10: 'subtitles',
        13: 'audiodescription',
      };

      const typeKey = assetTypes[data.type];

      for (let i = 0; i < this.allPods.length; i++) {
        if (this.allPods[i].id == data.podid) {
          if (!Array.isArray(this.allPods[i].asset[typeKey])) {
            this.allPods[i].asset[typeKey] = [];
          }

          let sameAsset = this.allPods[i].asset[typeKey].find(x => x.id == data.id);
          if (!sameAsset) {
            this.allPods[i].asset[typeKey].unshift(data);
          }
        }
      }
    },
    _updatePodAsset: function(data) {

      for (let i = 0; i < this.allPods.length; i++) {
        for (const property in this.allPods[i].asset) {

          for (let o = 0; o < this.allPods[i].asset[property].length; o++) {
            if (this.allPods[i].asset[property][o].id == data.id) {
              // Object.assign(this.allPods[i].asset[property][o], data);
              this.allPods[i].asset[property][o] = data;

              // no breaks here...
            }
          }
        }
      }
    },
    _deletePodAsset: function(payload) {
      const asset = payload.asset;

      for (let i = 0; i < this.allPods.length; i++) {
        for (const assetType in this.allPods[i].asset) {
          // creates a new collection of assets without the deleted asset.
          this.allPods[i].asset[assetType] = this.allPods[i].asset[assetType].filter((x) => x.id !== asset.id);

          // if the array type is empty, delete it.
          if (this.allPods[i].asset[assetType].length === 0) {
            delete this.allPods[i].asset[assetType];
          }
        }
      }

      this.selectedAssetIds = this.selectedAssetIds.filter((x) => x !== asset.id);
    },
    _deletePodAssets: function(deletedAssetIds) {
      for (let i = 0; i < this.allPods.length; i++) {
        for (const assetType in this.allPods[i].asset) {
          this.allPods[i].asset[assetType] =
          this.allPods[i].asset[assetType].filter(x => deletedAssetIds.indexOf(x.id) < 0);

          if (this.allPods[i].asset[assetType].length == 0) {
            delete this.allPods[i].asset[assetType];
          }
        }
      }

      this.selectedAssetIds = this.selectedAssetIds.filter(x => deletedAssetIds.indexOf(x) < 0);
    },
    _toggleSelectedPod: function(pod) {
      let idIndex = this.selectedPodIds.indexOf(pod.id);
      if (idIndex >= 0) {
        this.selectedPodIds.splice(idIndex, 1);
      } else {
        this.selectedPodIds.push(pod.id);
      }
    },
    _toggleSelectedAsset: function(asset) {
      let idIndex = this.selectedAssetIds.indexOf(asset.id);
      if (idIndex >= 0) {
        this.selectedAssetIds.splice(idIndex, 1);
      } else {
        this.selectedAssetIds.push(asset.id);
      }
    },
    _updateStateMessage: function(payload) {
      const index = this.allPods.findIndex(x => x.id === payload.podid);
      if (index >= 0) {
        this.allPods[index]['statemessage'] = payload.message;
      }
    },
    _removeLaunchedServices: function(payload) {
      const index = this.allPods.findIndex(x => x.id === payload.podid);
      if (index >= 0 && this.allPods[index]) {
        if (!this.allPods[index].launchedServices) {
          this.allPods[index].launchedServices = [];
        }

        this.allPods[index].launchedServices = this.allPods[index].launchedServices.filter(x => x.code !== payload.code);
      }
    },
    _deselectPod: function(podId) {
      const idx = this.selectedPodIds.indexOf(podId)
      if (idx >= 0) {
        this.selectedPodIds.splice(idx, 1);
      }
    },
    selectFilesInFolder(payload) {
      const { nodeId } = payload;

      if (nodeId) {
        const selectedPodIds = [];

        for (let i = 0; i < this.allPods.length; i++) {
          // Exclude versioned pods (pods that are versions of other files)
          // as they cannot be deleted directly and should not be selectable
          if (this.allPods[i].nodeid === nodeId && this.allPods[i].parentpodid == null) {
            selectedPodIds.push(this.allPods[i].id);
          }
        }

        this.selectedPodIds = selectedPodIds;
      }
    },
    toggleQuickSwitcher() {
      this.quickSwitcherVisible = !this.quickSwitcherVisible
    },
    addLatestDeletedPodId(podId) {
      if (!this.latestDeletedPodIds.includes(podId)) {
        this.latestDeletedPodIds.push(podId);
      }
    },
    removeLatestDeletedPodId(podId) {
      const index = this.latestDeletedPodIds.indexOf(podId);
      if (index >= 0) {
        this.latestDeletedPodIds.splice(index, 1);
      }
    },
    updateLaunchedServices: function(payload) {
      if (window.isNodeTransitioning) {
        return;
      }

      const index = this.allPods.findIndex(x => x.id === payload.podid);
      if (index >= 0) {
        this.allPods[index].launchedServices = payload.launchedServices;
      }

      if (payload.queue === 'end') {
        setTimeout(() => {
          this._removeLaunchedServices(payload);
        }, 3000);
      }
    },
    addLaunchedService: function(payload) {
      if (window.isNodeTransitioning) {
        return;
      }

      const index = this.allPods.findIndex(x => x.id === payload.podid);
      if (index >= 0) {
        if (!this.allPods[index].launchedServices) {
          this.allPods[index]['launchedServices'] = [];
        }

        const messageString = JSON.stringify(payload.message);
        const isExisting = this.allPods[index].launchedServices.find(function(x) {
          return JSON.stringify(x) === messageString;
        });

        if (!isExisting) {
          this.allPods[index].launchedServices.push(payload.message);
        }
      }
    },
    updateStateMessage: function(payload) {
      if (window.isNodeTransitioning) {
        return;
      }

      this._updateStateMessage(payload);

      if (payload.queue === 'end') {
        payload.message = null;

        setTimeout(() => {
          this._updateStateMessage(payload);
        }, 3000);
      }
    },
    refreshPods: async function(nodeID) {

      // When we have directory tree set entry point as header
      // Api.setEntryPoint("qqqqqqqqqqqqqq");
      // but that should be from the DirectoryStore...

      this._setViewPodId(null);

      try {
          const result = await Api.pod.getPodsByNodeID(nodeID)
          // console.log("API CALL");
          // console.log(result);
          this._addOrUpdatePods(result.data)
      } catch (error) {
          console.log("Error getting pods by node ID:")
          console.log(error)
      }
    },
    async fetchPodById(podId) {
      const response = await Api.pod.getPodByID(podId)
      const pod = response.data
      this._addOrUpdatePods(pod)
      return pod
    },
    fetchPodsByNodeId: async function(nodeID) {
      const result = await Api.pod.getPodsByNodeID(nodeID)
      this._addOrUpdatePods(result.data)
      return result.data
    },
    resetPods: function() {
      this.viewPodMeta = [];
      this.viewPodArchiveContents = null;
      this.viewPodListArchiveContentsError = null;
      this.isRefreshingPodArchiveContents = false;
      this._setViewPodActions([]);
      this.activeToolbar = 0;

      this._setViewPodId(null);

      const podCommentStore = usePodCommentStore();
      podCommentStore.resetComments();
    },
    setViewPodMoved: function(isMoved) {
      this.viewPodMoved = isMoved;
    },
    setViewPodDeleted: function(isDeleted) {
      this.viewPodDeleted = isDeleted;
    },
    setViewPodArchived: function(isArchived) {
      this.viewPodArchived = isArchived;
    },
    refreshPodMeta: async function(podID) {
      const response = await Api.pod.getPodMeta(podID);
      this.viewPodMeta = response.data;
      return response.data;
    },
    /**
     * @param {string} podID
     * @param {AbortController} [abortController]
     * @returns {Promise<*>}
     */
    viewPod: async function (podID, abortController) {
      try {
        const getPodResponse = await Api.pod.getPodByID(podID, abortController);
        const pod = getPodResponse.data;

        this._setViewPodId(pod.id);
        this._addOrUpdatePods(pod);
        this.selectedPodIds.push(pod.id);

        const getPodsByNodeIdResponse = await Api.pod.getPodsByNodeID(pod.nodeid, abortController);
        const pods = getPodsByNodeIdResponse.data;

        if (Array.isArray(pods) && pods.length > 0) {
          const podsInNode = pods.filter(x => x.id !== pod.id);
          this._addOrUpdatePods(podsInNode);
        }

        this.maybeRefreshViewPodArchiveContents().catch((error) => {
          console.error(error);
          this.viewPodListArchiveContentsError = error.message;
        });

        return pod;
      } catch (error) {
        console.log('Error getting pod by id.');
        console.log(error);
      }
    },
    createPod: async function(podData) {
      try {
          const result = await Api.pod.createPod(podData)
          const pod = result.data
          this._addOrUpdatePods(pod)
          return pod
      } catch (error) {
          console.log("Error creating pod:")
          console.log(error)
          return await Promise.reject(error)
      }
    },
    createManyPods: async function(podsData) {
      try {
        const result = await Api.pod.createManyPods(podsData)
        const createdPods = result.data;
        this._addOrUpdatePods(createdPods);
        return createdPods;
      } catch (error) {
        if (error?.response?.data?.error) {
          if (error.response.data.error.status === 400 && error.response.data.error.duplicateUuids) {
            throw new CreateManyPodsDuplicateUuidsError(error.response.data.error.duplicateUuids);
          }
        }

        console.error('Error during creation of many pods: ', error);
        throw error;
      }
    },
    preCreateManyPods: async function(podsData) {
      try {
        const result = await Api.pod.preCreateManyPods(podsData);
        return result.data;
      } catch (error) {
        console.error('Error during pre-creation of many pods: ', error);
        throw error;
      }
    },
    updatePod: async function(podData) {
      const result = await Api.pod.updatePod(podData);
      this._addOrUpdatePods(result.data);
      this.updateOtherStoresPod(result.data);
      return result.data;
    },
    updatePodNote: async function(payload) {
      const result = await Api.pod.updatePodNote(payload)
      this._addOrUpdatePods(result.data)
      this.updateOtherStoresPod(result.data)
      return result.data
    },
    updateBadgeStatus: async function(payload) {
      const result = await Api.pod.updateBadgeStatus(payload)
      this._addOrUpdatePods(result.data)
      this.updateOtherStoresPod(result.data)
      return result.data
    },
    updateOtherStoresPod(pod) {
      const searchStore = useSearchStore();
      searchStore.updateSearchResultPod(pod);

      const recycleBinStore = useRecycleBinStore();
      recycleBinStore.updateRecycleBinPod(pod);
    },
    transcodeFile: async function(data) {
      const result = await Api.pod.transcodeFile(data)
      return result.data
    },
    extractArchive: async function(data) {
      const result = await Api.pod.extractArchive(data)
      return result.data
    },
    generateAudio: async function(data) {
      const result = await Api.pod.generateAudio(data)
      return result.data
    },
    deletePods: async function(podIDs, permanently = false) {
      if (podIDs.length === 1) {
        await Api.pod.deletePod(podIDs[0], permanently);
      } else {
        await Api.pod.deleteManyPods(podIDs, permanently);
      }

      this._deletePods(podIDs);
    },
    createPodAsset: async function(data) {
      try {
        const result = await Api.pod.createPodAsset(data)
        this._addPodAsset(result.data)
        return result.data
      } catch (error) {
        console.log("Error creating podAsset:")
        console.log(error)
        // return await Promise.reject(error)
        throw error;
      }
    },
    updatePodAsset: async function(data) {
      let result = await Api.pod.updatePodAsset(data);
      this._updatePodAsset(result.data);

      const playerStore = usePlayerStore();
      await playerStore.refreshSubtitles(this.getViewPod);

      return result.data;
    },
    deletePodAssets: async function(data) {
      let promises = data.map(x => Api.pod.deletePodAsset(x.podid, x.id));
      let results = await Promise.all(promises);
      // results[i].data is a pod
      results;

      const assetIds = data.map(x => x.id);
      this._deletePodAssets(assetIds);

      // remove srt/vtt active subtitle
      const playerStore = usePlayerStore();
      await playerStore.refreshSubtitles(this.getViewPod);
    },
    createSubtitleAsset: async function(data) {
      let result = await Api.pod.createSubtitleAsset(data);
      this._addPodAsset(result.data);

      const playerStore = usePlayerStore();
      await playerStore.refreshSubtitles(this.getViewPod);

      return result.data;
    },
    updateSubtitleAsset: async function (data) {
      let result = await Api.pod.updateSubtitleAsset(data);
      this._updatePodAsset(result.data);

      const playerStore = usePlayerStore();
      await playerStore.refreshSubtitles(this.getViewPod);

      // Return the full response including any warnings
      return result.data;
    },
    setAssetLanguage: async function(data) {
      const response = await Api.pod.setAssetLanguage(data.podID, data.assetID, data.languageID);
      // console.log("Updated asset language", response);
      this._updatePodAsset(response.data);
    },
    toggleSelectedPod: function(pod) {
      this._toggleSelectedPod(pod);
    },
    deselectAllPods: function() {
      this.selectedPodIds = [];
    },
    refreshPodActions: async function(podId) {
      let response = await Api.pod.getPodActions(podId)
      this._setViewPodActions(response.data);
      return response.data;
    },
    toggleSelectedAsset: function(asset) {
      this._toggleSelectedAsset(asset);
    },
    promoteAssetToPod: async function(asset) {
      const authStore = useAuthStore();
      const USERUUID = authStore.user.userId;

      const payload = {
        uuid: uuidv4(),
        useruuid: USERUUID,
      };

      const data = {
        payload,
        assetID: asset.id,
        podID: asset.podid,
      }

      const result = await Api.pod.promoteAssetToPod(data)
      return result.data
    },
    moveManyPods: async function(payload) {
      const response = await Api.pod.moveManyPods(payload);
      this._addOrUpdatePods(response.data);
      response; // not used
    },
    copyManyPods: function(payload) {
      return Api.pod.copyManyPods(payload);
    },
    copyManyPodsCrossTenant: async function(payload) {
      return Api.pod.copyManyPodsCrossTenant(payload);
    },
    convertSubtitle: async function(asset) {
      const payload = {};

      const data = {
        payload,
        assetID: asset.id,
        podID: asset.podid,
      }

      const result = await Api.pod.convertSubtitle(data)
      return result.data
    },
    addVersion: async function(payload) {
      const response = await Api.pod.addVersion(payload)
      this._addOrUpdatePods(response.data)

      for (let i = 0; i < response.data.length; i++) {
        this.updateOtherStoresPod(response.data[i])
      }
    },
    reorderVersions: async function(payload) {
      const response = await Api.pod.reorderVersions(payload)
      this._addOrUpdatePods(response.data)

      for (let i = 0; i < response.data.length; i++) {
        this.updateOtherStoresPod(response.data[i])
      }

      if (payload.removeid) {
        for (let i = 0; i < this.allPods.length; i++) {
          if (this.allPods[i].id == payload.removeid) {
            this.allPods[i].parentpodid = null;
            this.allPods[i].order = null;
            this.allPods[i].versiontext = null;
          }
        }
      }
    },
    addOrUpdatePods: function(pods) {
      this._addOrUpdatePods(pods);
    },
    addOrUpdatePod: function(pod) {
      this._addOrUpdatePods(pod);
      this.updateOtherStoresPod(pod);

      // TODO: Check if isNodeTransitioning is still needed
      if (window.isNodeTransitioning) {
        return;
      }

      if (this.getViewPod && this.getViewPod.id === pod.id) {
        if (this.getViewPod.archived == 1) {
          if (this.viewPodId && this.viewPodId == pod.id) {
            this.viewPodArchived = true;
          }
        }

        const playerStore = usePlayerStore();
        playerStore.refreshSubtitles(this.getViewPod);
      }
    },
    updateOrIgnorePod: function(pod) {
      this._addOrUpdatePods(pod);
      this.updateOtherStoresPod(pod);
    },
    podMovedNotification: function(pods) {
      const viewPodId = this.viewPodId;

      this._addOrUpdatePods(pods);

      const pod = pods.find((x) => x.id === viewPodId);
      if (pod) {
        this.viewPodMoved = true;
      }
    },
    podCopiedNotification: function(pods) {
      this._addOrUpdatePods(pods);
    },
    podDeletedNotification: function(pods) {
      let podIds = Array.isArray(pods) ? pods.map((p) => p.id) : [];

      // remove duplicates and empty values
      podIds = [...new Set(podIds)].filter(Boolean);
      this._deletePods(podIds);
      this.refreshRecycleBin();
    },

    /**
     * Event handler for pod blocked event.
     * @param pods
     */
    triggerPodBlockedEvent: function(pods) {
      let podIds = Array.isArray(pods) ? pods.map((p) => p.id) : [];

      // remove duplicates and empty values
      podIds = [...new Set(podIds)].filter(Boolean);
      this._deletePods(podIds);

      const uploadStore = useUploadStore();
      uploadStore.setBlockedFiles(podIds);
    },
    refreshRecycleBin() {
      const authStore = useAuthStore();
      const getRootNodeId = authStore.rootNodeId;

      if (getRootNodeId) {
        const payload = {
          rootNodeId: getRootNodeId,
          silent: true,
        };

        if (window.preventRecycleBinRefresh) {
          return;
        }

        window.preventRecycleBinRefresh = true;

        // make only 1 recycle bin refresh per second
        setTimeout(() => {
          const recycleBinStore = useRecycleBinStore();
          recycleBinStore.refreshNodesList(payload);
          window.preventRecycleBinRefresh = false;
        }, 1000);
      }

    },

    removePodsByNodeDeleted: function(nodes) {
      const recycleBinStore = useRecycleBinStore();

      let nodeIdsForDeletion;
      if (Array.isArray(nodes)) {
        nodeIdsForDeletion = nodes.map((n) => n.id);
      } else {
        nodeIdsForDeletion = nodes.childIds ? [nodes.id, ...nodes.childIds] : [nodes.id];
      }

      const recycleBinDeletedNodeIds = recycleBinStore.getAllNodes
        .filter(n => n.deletedat !== null).map(n => n.id);
      nodeIdsForDeletion = nodeIdsForDeletion.concat(recycleBinDeletedNodeIds);

      let deletePods = [];
      for (let i = 0; i < nodeIdsForDeletion.length; i++) {
        deletePods = deletePods.concat(this.getPodsByNodeId(nodeIdsForDeletion[i]));
      }

      this.podDeletedNotification(deletePods);
    },
    setActiveToolbar(toolbarID) {
      this.activeToolbar = toolbarID;
    },
    updateTeamOnly: async function(payload) {
      const result = await Api.pod.updateTeamOnly(payload);
      // this._addOrUpdatePods(result.data);
      // this.updateOtherStoresPod(result.data);
      return result.data;
    },
    updateTeamOnlyPods: async function(payload) {
      return await Api.pod.updateTeamOnlyPods(payload)
    },
    renameFile: async function(payload) {
      const result = await Api.pod.renameFile(payload);
      this._addOrUpdatePods(result.data);
      this.updateOtherStoresPod(result.data);
      return result.data;
    },
    setPodStatus(payload) {
      const idx = this.allPods.findIndex(x => x.id === payload.podid);
      if (idx > -1) {
        this.allPods[idx]['status'] = {
          status: payload.status,
          metadata: payload.metadata,
        };
      }

      const stateMessage = this.getStatusStateMessage(payload.podid);
      if (stateMessage) {
        this.updateStateMessage({
          podid: payload.podid,
          message: stateMessage,
        });
      }
    },
    convertImage: async function(payload) {
      return await Api.pod.convertImage(payload);
    },
    maybeRefreshViewPodArchiveContents: async function () {
      if (!this.isArchivePod) {
        return;
      }

      this.isRefreshingPodArchiveContents = true;

      try {
        const contents = await Api.pod.listArchiveContents(this.viewPodId);
        if (contents !== null) {
          this.setViewPodArchiveContents(contents);
        }
      } catch (err) {
        console.error(err);
        this.viewPodListArchiveContentsError = err.message;
        this.isRefreshingPodArchiveContents = false;
      }
    },
    setViewPodArchiveContents: function(contents) {
      if (this.isArchivePod) {
        this.viewPodArchiveContents = contents;
        this.isRefreshingPodArchiveContents = false;
      }
    },
    setViewPodListArchiveContentsError: function (error) {
      if (this.isArchivePod) {
        this.viewPodListArchiveContentsError = error;
        this.isRefreshingPodArchiveContents = false;
      }
    }
  },
});
